import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SalesEntityService } from '../../layout/sales-entity/sales-entity-service/sales-entity-service';
import { IEvoSystem } from '../models/AuthModels';
import { EnhApplicationAgentsResponse, ISalesEntityBoardTargets } from '../models/boardModels';
import { IUserDetail } from '../models/UserDetailModel';
import { LookupEnvironmentService } from './lookup-environment.service';
import { SessionService } from './session.service';
import { Router, UrlSegment } from '@angular/router';
import { CacheService } from './cache.service';
import { RolePermission } from '../models/Securable';
import { Observable } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';


@Injectable()
/**
 * Service responsible for securables / User access to the system and to parts of the system.
 */
export class UserAccessService {
	public readonly securablePrefix: string = 'ESP|';
	private redirectLocation: string;

	private _canBoard = false;
	get canBoard(): boolean {
		return this._canBoard;
	}
	private _UserName = null;
	get UserName(): string {
		return this._UserName;
	}
	// private _mySalesEntityID: number;
	// get mySalesEntityID(): number {
	// 	return this._mySalesEntityID;
	// }

	constructor(
		private salesEntitySvc: SalesEntityService,
		private authService: SessionService,
		private environment: LookupEnvironmentService,
		private cacheService: CacheService,
		private router: Router,
		private http: HttpClient
	) {
		this.getCanBoard();
		this.getUserAccess();
		this.getUserDetail();
	}

	//#region Securables

	/**
	 * If user has no ESP permissions kicks them to error page.
	 * Otherwise kicks user to their default homepage.
	 * @warning Hardcoded to a 404 page for now until we determine homepage.
	 */
	public UnauthorizedRedirect() {
		// By the first call to this function this get is cached already.
		this.getSecurables('ESP', 1).toPromise().then((resp) => {
			let espTemp = this.FindSecurableByName('ESP', resp);
			// When checking ROUTES, if they don't have ESP kick them out to a permissions error
			if (espTemp.AccessLevel < 1) {
				this.PermissionsRedirect();
			} else {
				// Figure out the users default homepage and bump them back to that.
				console.log('User is not authenticated to this route, redirecting to unauthorized location. DEFAULT IS DASHBOARD TODO:CHANGE.');
				if (this.redirectLocation === undefined || this.redirectLocation === '') {
					this.router.navigate(['not-found']);
				}
				this.router.navigate([this.redirectLocation]);
			}
		}).catch((response: HttpErrorResponse) => {
			console.log(response.statusText);
		});
	}

	public PermissionsRedirect() {
		console.log('User has no access to ESP, redirecting to permissions error page');
		this.router.navigate(['error']);
	}

	/**
	 * This function is only used for the Authentication Guards on routes (See secured.route.ts)
	 * @param desiredRoute The route being activated ex: 'dashboard'
	 * @returns Observable<boolean> whether or not they can access the desired resource
	 */
	public canAccess(desiredRoute: string): Observable<boolean> {
		const name = this.buildWholeSecurableName(undefined, desiredRoute);
		// console.log("Component Securable Guarding For: ", name);
		return this.getSecurables('ESP', 1).pipe(map(resp => {
			try {
				// If they have ESP access continue checking their specific access.
				let temp = this.FindSecurableByName(name, <Array<RolePermission>>resp);
				// console.log("ACCESS OBJECT:", temp);
				if (temp.AccessLevel >= 1) { // They have read or better access to it.
					console.log('%cAuthenticated to Route: ', 'background-color:green;color:white;', name);
					return true;
				} else { // They have the permission but not the right access to it.
					console.log('%cNOT Authenticated to Route: ', 'background-color:red;color:white;', name);
					return false;
				}
			} catch (err) { // They didn't have the permission at all and it tossed an error.
				console.log('%cNOT Authenticated to Route: ', 'background-color:red;color:white;', name, err);
				return false;
			}
		}));
	}

	/**
	 * Finds a securable in a list of securables by name
	 * @param securableName The name of the securable you're searching for.
	 * @param source The array of securables to search inside.
	 */
	private FindSecurableByName(securableName: string, source: Array<RolePermission>) {
		for (let i = 0; i < source.length; i++) {
			if (source[i].SecurableName === securableName) { return source[i]; }
		}
		return undefined;
	}

	/**
	 * Gets the AccessLevel property of the target securable.
	 * @param securableName The name of the securable (the ID of the target element).
	 * @param optionalRoute The optional route to use in place of the current router route. (Must contain all separation pipes).
	 * @param optionalSeID 	The optional Sales Entity ID for SE specific permissions.
	 * @param optionalMerchantID The optional merchant ID for MID specific permissions.
	 * @param secNameIsComplete Whether securable name is the entire name, or we build it from the route/optional route.
	 * @returns AccessLevel of the target securable. Ex: 1 (read only)
	 */
	public async GetAccessLevelBySecurableName(securableName: string, optionalRoute?: string, optionalSeID?: number, optionalMerchantNumber?: number, secNameIsComplete: boolean = false): Promise<number> {
		// This will build the securable name using the passed in name and route then
		// throws it at the cache svc. The cache svc merges duplicate requests.
		let name = this.buildWholeSecurableName(securableName, optionalRoute);
		if (secNameIsComplete) {
			name = this.securablePrefix + securableName;
		}
		// console.log("SecurableName: ", securableName);
		// console.log("OptionalRoute: ", optionalRoute);
		// console.log("BUILT NAME: ", name);
		if (optionalRoute === '') {
			// High level naviation menu items only which is why we pass full depth 1
			return this.getSecurables('ESP', 1, optionalSeID, optionalMerchantNumber).pipe(
				map(resp => {
					try {
						const temp = this.FindSecurableByName(name, <Array<RolePermission>>resp);
						return temp.AccessLevel;
					} catch (err) { // They didn't have the permission at all and it tossed an error.
						// console.log("NOT Authenticated to: ", name);
						return 0;
					}
				})).toPromise();
		} else if (optionalRoute) {
			// We are given an optional route so you need to call for that route and give back the securable from that route.
			return this.getSecurables(name, undefined,
				optionalSeID, optionalMerchantNumber).pipe(map(resp => {
					try {

						const temp = this.FindSecurableByName(name, resp as Array<RolePermission>);
						return temp.AccessLevel;
					} catch (err) { // They didn't have the permission at all and it tossed an error.
						// console.log("NOT Authenticated to: ", name);
						return 0;
					}
				})).toPromise();
		} else {
			// There are cases where securable name is complete and there is no route...
			if (secNameIsComplete) {
				return this.getSecurables(this.securablePrefix + securableName.toUpperCase(), undefined,
					optionalSeID, optionalMerchantNumber).pipe(map(resp => {
						try {
							const temp = this.FindSecurableByName(name, resp as Array<RolePermission>);
							return temp.AccessLevel;
						} catch (err) { // They didn't have the permission at all and it tossed an error.
							// console.log("NOT Authenticated to: ", name);
							return 0;
						}
					})).toPromise();
			} else {
				// Make a call for the page based on the route and find the securable within that page
				return this.getSecurables(this.securablePrefix + this.getBaseRouteOfURL(), undefined,
					optionalSeID, optionalMerchantNumber).pipe(map(resp => {
						try {
							const temp = this.FindSecurableByName(name, resp as Array<RolePermission>);
							return temp.AccessLevel;
						} catch (err) { // They didn't have the permission at all and it tossed an error.
							// console.log("NOT Authenticated to: ", name);
							return 0;
						}
					})).toPromise();
			}

		}
	}
	/**
	 * Gets a whole rolepermission object by name
	 * @param securableName The name of the securable (the ID of the target element).
	 * @param optionalRoute The optional route to use in place of the current router route. (Must contain all separation pipes).
	 * @param optionalSeID 	The optional Sales Entity ID for SE specific permissions.
	 * @param optionalMerchantID The optional merchant ID for MID specific permissions.
	 * @param secNameIsComplete Whether securable name is the entire name, or we build it from the route/optional route.
	 * @param optionalSubObjID The optional Sub Object ID (AddressID/ContactID etc)
	 * @param optionalSecurableObjID The optional SecurableObjID (The parent of SubObjID required when sending SubObjID)
	 */
	public GetSecurableObjectByName(securableName: string, optionalRoute?: string, optionalSeID?: number, optionalMerchantNumber?: number, secNameIsComplete: boolean = false, optionalSubObjID?: number, optionalSecurableObjID?: number): Promise<RolePermission> {
		if (optionalSubObjID && !optionalSecurableObjID) { console.error('NO SECURABLE OBJECT ID PROVIDED WHEN TRYING TO GET SECURABLES FOR SUB OBJECT.'); }
		// This will build the securable name using the passed in name and route then
		// throws it at the cache svc. The cache svc merges duplicate requests.
		let name = this.buildWholeSecurableName(securableName, optionalRoute);
		if (secNameIsComplete) {
			// Add the prefix of ESP to anything deemed a whole name
			name = this.securablePrefix + securableName.toUpperCase();
		}
		// console.log("SecurableName:",name);
		// console.log("SecName is complete: ", secNameIsComplete);
		if (optionalRoute === '') {
			// Only occurs for the high level MENU items
			return this.getSecurables('ESP', 1, optionalSeID, optionalMerchantNumber, optionalSubObjID, optionalSecurableObjID).pipe(map(resp => {
				try {
					return this.FindSecurableByName(name, <Array<RolePermission>>resp);
				} catch (err) {
					return undefined;
				}
			})).toPromise();
		} else {
			// console.log("GetSecurableObjectByName: ", securableName, optionalRoute, optionalSeID, optionalMerchantNumber);
			// More likely of the two for securables
			return this.getSecurables(this.securablePrefix + this.getBaseRouteOfURL(), undefined, optionalSeID, optionalMerchantNumber, optionalSubObjID, optionalSecurableObjID).pipe(map(resp => {
				try {
					return this.FindSecurableByName(name, <Array<RolePermission>>resp);
				} catch (err) {
					return undefined;
				}
			})).toPromise();
		}
	}

	/**
	 * Gets the [1] entry of current route.split('/') or the same out of the passed optionalRoute
	 * @param optionalRoute A route if provided will be used in place of the router current route.
	 * @returns if route == '/dashboard/drilldown' returns 'DASHBOARD'
	 */
	public getBaseRouteOfURL(optionalRoute?: string): string {
		if (optionalRoute === undefined) { optionalRoute = this.router.url; }
		const urlPieces: string[] = optionalRoute.toUpperCase().split('/');
		// The url always contains a single slash eg: /residuals
		return urlPieces[1];
	}

	/**
	 * Builds the securable name from the prefix, the optionalRoute or the Router current route, and the securable name
	 * @param securableName The name of the securable (the ID of the target element)
	 * @param optionalRoute The optional route to use in place of the current router route. (Must contain all separation pipes)
	 * @returns 'ESP|' + [optionalRoute or the router route + '|'] + securableName
	 */
	private buildWholeSecurableName(securableName?: string, optionalRoute?: string): string {
		let workingName = this.securablePrefix; // start with prefix
		if (securableName === undefined || securableName === '') {
			// if there's no name, just use route eg "ESP|DASHBOARD"
			workingName = this.securablePrefix + optionalRoute;
			if (workingName === 'ESP|') { workingName = 'ESP'; }
		} else {
			securableName = securableName.toUpperCase();
			if (optionalRoute !== undefined) {
				// console.log("OptionalRoute has value: ", securableName, optionalRoute);
				optionalRoute = optionalRoute.toUpperCase();
				workingName = workingName + optionalRoute + securableName;
				// console.log("workingName: ", workingName);
			} else {
				workingName = workingName + this.getBaseRouteOfURL() + '|' + securableName;
				// console.log("workingName: ", workingName);
			}
		}
		// console.log(workingName);
		return workingName;
	}

	/**
	 * Gets securables from the backend
	 * @param route The optional full securable name
	 * @param depth The optional depth of the tree to search
	 * @param optionalSeID The optional SalesEntityID
	 * @param optionalMerchantNumber The optional MerchantID
	 * @param optionalSubObjID The optional Sub Object ID (AddressID/ContactID etc)
	 * @param optionalSecurableObjID The optional SecurableObjID (The parent of SubObjID required when sending SubObjID)
	 * @returns An Observable of an array of securables
	 */
	private getSecurables(name: string, depth?: number, optionalSeID?: number, optionalMerchantNumber?: number, optionalSubObjID?: number, optionalSecurableObjID?: number): Observable<Array<RolePermission>> {
		let url = 'api/auth/getusersecurables?secname=';
		url = url + encodeURI(name);
		if (depth) { url = url + '&fulldepth=' + depth; }
		if (optionalSeID) { url = url + '&SeID=' + optionalSeID; }
		if (optionalMerchantNumber) { url = url + '&merchantNumber=' + optionalMerchantNumber; }
		if (optionalSubObjID) { url = url + '&objectID=' + optionalSubObjID; } // Example: Contact ID / Address ID
		if (optionalSecurableObjID) { url = url + '&objectSecurableID=' + optionalSecurableObjID; } // The parent ID which tells the sproc what the subobjID is
		// console.log("GetSecurablesFromCacheSvc: ", name, depth, optionalSeID, optionalMerchantNumber, url);
		return this.cacheService.get(url, this.http.get(url), 600000);
	}

	/**
	 * Refreshes the cache by rebuilding the call and making it again immediately.
	 * @param route The optional full securable name
	 * @param depth The optional depth of the tree to search
	 * @param optionalSeID The optional SalesEntityID
	 * @param optionalMerchantNumber The optional MerchantID
	 * @param optionalSubObjID The optional Sub Object ID (AddressID/ContactID etc)
	 * @param optionalSecurableObjID The optional SecurableObjID (The parent of SubObjID required when sending SubObjID)
	 */
	public Securable_RefreshSectionCache(route?: string, depth?: number, optionalSeID?: number, optionalMerchantNumber?: number, optionalSubObjID?: number, optionalSecurableObjID?: number) {
		let name = this.securablePrefix;
		if (route) { name = name + route; } else { name = name + this.getBaseRouteOfURL(); }
		let url = 'api/auth/getusersecurables?secname=';
		url = url + encodeURI(name);
		if (depth) { url = url + '&fulldepth=' + depth; }
		if (optionalSeID) { url = url + '&SeID=' + optionalSeID; }
		if (optionalMerchantNumber) { url = url + '&merchantNumber=' + optionalMerchantNumber; }
		if (optionalSubObjID) { url = url + '&objectID=' + optionalSubObjID; } // Example: Contact ID / Address ID
		if (optionalSecurableObjID) { url = url + '&objectSecurableID=' + optionalSecurableObjID; } // The parent ID which tells the sproc what the subobjID is
		console.log('Securable_RefreshCache:', url);
		this.cacheService.refreshCache_GET(url, 60000);
	}
	//#endregion

	//#region UserAccess
	getCanBoard() {
		const promise = this.salesEntitySvc
			.GetApplicationSalesEntities()
			.toPromise();
		promise
			.then((targets: EnhApplicationAgentsResponse) => {
				// this._mySalesEntityID = targets.EnhAgentInfo[0].ISO_ID;
				console.log('GOT BOARD TARGETS: ', targets);
				// Board targets > 0
				if (targets.EnhAgentInfo && (targets.EnhAgentInfo.length > 0)) {
					this._canBoard = true;
				} else {
					// Board targets can be empty if the set is too large to consume
					if (targets.FilterRequired) {
						this._canBoard = true;
					}
				}
			})
			.catch((response: HttpErrorResponse) => {
				console.log(response);
			});
	}

	public getUserAccess() {
		const promise = this.authService.getUserAccess().toPromise();
		promise
			.then((arr: Array<IEvoSystem>) => {
				const index = arr.findIndex(
					x => x.SystemId.toLowerCase() === 'salesportal'
				);
				if (-1 === index) {
					this.redirect();
				}
			})
			.catch((response: HttpErrorResponse) => {
				console.log(response.statusText);
			});
	}

	public getUserDetail() {
		const promise = this.authService.getUserDetail().toPromise();
		promise
			.then((result: IUserDetail) => {
				if (result.FirstName != null && result.LastName != null) {
					this._UserName = result.FirstName + ' ' + result.LastName;
				}
			})
			.catch((response: HttpErrorResponse) => {
				console.log(response.statusText);
			});
	}


	private redirect() {
		// redirect to the shared login application
		let href = window.location.href;
		href = href.replace('logout', '');
		const lookupEnv = this.environment.getConfig();
		const authUrl = lookupEnv.authWebURL
			? lookupEnv.authWebURL
			: '/EvoAuthWeb/';
		window.location.href = authUrl + '#/catalog';
	}

	//#endregion

}
